/**
 * OrdersWidget — analytics-v2 order volume & GMV trend (CORE-1201).
 *
 * Engineering-lane widget — receives `environment` prop from the dashboard's
 * global env selector. DB data is env-scoped (dev has test data, prod has
 * real orders), making this the only analytics-v2 widget where the env
 * selector is genuinely meaningful.
 *
 * Shows:
 *   - Summary stat row: total orders, total GMV, avg order value for the window
 *   - Daily order volume as a CSS bar chart (no external chart dep)
 *   - Attribution caption + last updated timestamp
 *
 * Null handling:
 *   - `avgOrderValue === null` → "—" (totalOrders = 0, no meaningful average)
 *   - `dailyRows.length === 0` → empty-state message, chart suppressed
 */

import { useState } from 'react';
import { getAnalyticsOrders } from '@/api/client';
import { formatCount, formatCurrency, formatDateTime } from '@/utils/format';
import { shortDate } from '@/utils/shortDate';
import { useAnalyticsV2Fetch } from '@/hooks/useAnalyticsV2Fetch';
import { attributionCaption } from '@/utils/attribution';
import { AnalyticsV2ErrorBoundary } from './AnalyticsV2ErrorBoundary';
import type { Environment, FunnelWindow, OrdersDayRow } from '@/types/api';

const WINDOW_OPTIONS: readonly { value: FunnelWindow; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

interface OrdersWidgetProps {
  environment: Environment;
}

export function OrdersWidget({ environment }: OrdersWidgetProps): JSX.Element {
  const [selectedWindow, setSelectedWindow] = useState<FunnelWindow>('30d');

  return (
    <AnalyticsV2ErrorBoundary
      title="Order Volume & GMV"
      resetKey={`${environment}-${selectedWindow}`}
    >
      <OrdersWidgetBody
        environment={environment}
        selectedWindow={selectedWindow}
        onWindowChange={setSelectedWindow}
      />
    </AnalyticsV2ErrorBoundary>
  );
}

interface OrdersWidgetBodyProps {
  environment: Environment;
  selectedWindow: FunnelWindow;
  onWindowChange: (w: FunnelWindow) => void;
}

function OrdersWidgetBody({
  environment,
  selectedWindow,
  onWindowChange,
}: OrdersWidgetBodyProps): JSX.Element {
  const { data, error, isLoading } = useAnalyticsV2Fetch(
    () => getAnalyticsOrders({ env: environment, window: selectedWindow }),
    [environment, selectedWindow],
  );

  return (
    <section className="orders-widget">
      <header className="orders-widget-header">
        <h3>
          Order Volume &amp; GMV
          {/*
            aria-hidden on the badge — the accessible-name algorithm
            concatenates all descendant text, so without this screen readers
            would announce "Order Volume & GMV dev". The sr-only span below
            carries the env announcement as real text content.
          */}
          <span className="analytics-v2-env-badge" aria-hidden="true">
            {environment}
          </span>
        </h3>
        <span className="sr-only">Environment: {environment}</span>
      </header>

      <div className="analytics-v2-widget-controls">
        <label>
          Window
          <select
            className="analytics-v2-widget-select"
            value={selectedWindow}
            onChange={(e) => onWindowChange(e.target.value as FunnelWindow)}
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isLoading && (
        <p className="orders-widget-loading">Loading order data…</p>
      )}

      {!isLoading && error !== null && (
        <div className="orders-widget-error" role="alert">
          Could not load order data: {error}
        </div>
      )}

      {!isLoading && error === null && data !== null && (
        <>
          <p className="orders-widget-caption">
            Attribution: {attributionCaption(data.attributionWindow)}. Last updated{' '}
            {formatDateTime(data.lastUpdated)}.
          </p>

          <div className="analytics-stat-grid">
            <div className="analytics-stat">
              <span className="analytics-stat-value">
                {data.totalOrders.toLocaleString()}
              </span>
              <span className="analytics-stat-label">Total orders</span>
            </div>
            <div className="analytics-stat">
              <span className="analytics-stat-value">
                {formatCurrency(data.totalGmv, 'USD')}
              </span>
              <span className="analytics-stat-label">Total GMV</span>
            </div>
            <div className="analytics-stat">
              <span className="analytics-stat-value">
                {data.avgOrderValue === null
                  ? '—'
                  : formatCurrency(data.avgOrderValue, 'USD')}
              </span>
              <span className="analytics-stat-label">Avg order value</span>
            </div>
          </div>

          {data.dailyRows.length === 0 ? (
            <p className="empty-state" role="status">
              No order data for this window.
            </p>
          ) : (
            <OrdersTrendChart rows={data.dailyRows} />
          )}
        </>
      )}
    </section>
  );
}

/** CSS bar chart of daily order counts. No external charting library required. */
function OrdersTrendChart({ rows }: { rows: OrdersDayRow[] }): JSX.Element {
  const maxCount = Math.max(...rows.map((r) => r.orderCount), 1);

  return (
    <div className="orders-widget-chart" aria-label="Daily order volume chart" role="img">
      {rows.map((row) => {
        const heightPct = (row.orderCount / maxCount) * 100;
        return (
          <div
            key={row.date}
            className="orders-widget-chart-col"
            title={`${row.date}: ${formatCount(row.orderCount)} orders, ${formatCurrency(row.gmv, 'USD')}`}
          >
            <div
              className="orders-widget-chart-bar"
              style={{ height: `${heightPct}%` }}
              aria-hidden="true"
            />
            <span className="orders-widget-chart-label">{shortDate(row.date)}</span>
          </div>
        );
      })}
    </div>
  );
}

